import React, { useState } from 'react';
import styled from 'styled-components';
import CurrencyInput from 'react-currency-input-field';
import currency from 'helpers/currency';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import { campaign } from './shared';

const MARKET_FEE = 0.10;
const WHIZR_FEE_PIX = 0.06;
const WHIZR_FEE_CARD = 0.08;

const PRESETS = [5000, 10000, 30000];

export default function FeeCalculator() {
    const [amount, setAmount] = useState(10000);

    const marketFee = amount * MARKET_FEE;
    const rows = [
        { label: 'no Pix', rate: '6%', whizrFee: amount * WHIZR_FEE_PIX },
        { label: 'no cartão', rate: '8%', whizrFee: amount * WHIZR_FEE_CARD },
    ];

    return (
        <Wrapper>
            <Label htmlFor="calc-amount">Quanto você espera vender no evento?</Label>

            <InputRow>
                <Prefix>R$</Prefix>
                <StyledInput
                    id="calc-amount"
                    value={amount}
                    decimalsLimit={2}
                    decimalSeparator=","
                    groupSeparator="."
                    onValueChange={(_value, _name, values) => setAmount(values?.float ?? 0)}
                />
            </InputRow>

            <PresetRow>
                {PRESETS.map((preset) => (
                    <PresetChip
                        key={preset}
                        type="button"
                        $active={amount === preset}
                        onClick={() => setAmount(preset)}
                    >
                        {currency.format(preset).replace(',00', '')}
                    </PresetChip>
                ))}
            </PresetRow>

            <Table>
                <TableHead>
                    <span>Método</span>
                    <span>Mercado (10%)</span>
                    <span>Whizr</span>
                    <span>Você economiza</span>
                </TableHead>

                {rows.map((row) => (
                    <TableRow key={row.label}>
                        <MethodLabel>{row.label}</MethodLabel>
                        <OldFee>{currency.format(marketFee)}</OldFee>
                        <NewFee>
                            {currency.format(row.whizrFee)}
                            <RateTag>{row.rate}</RateTag>
                        </NewFee>
                        <Savings>{currency.format(marketFee - row.whizrFee)}</Savings>
                    </TableRow>
                ))}
            </Table>
        </Wrapper>
    );
}

const Wrapper = styled.div`
    width: 100%;
`;

const Label = styled.label`
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: ${colors.night};
    margin-bottom: 8px;
`;

const InputRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    background: ${colors.neutralBackground};
    border: 1.5px solid ${colors.night}22;
    border-radius: 12px;
    padding: 10px 14px;
    margin-bottom: 12px;

    &:focus-within {
        border-color: ${campaign.purple};
    }
`;

const Prefix = styled.span`
    font-size: 18px;
    font-weight: 700;
    color: ${colors.night};
`;

const StyledInput = styled(CurrencyInput)`
    flex: 1;
    border: none;
    background: transparent;
    outline: none;
    font-size: 18px;
    font-weight: 700;
    color: ${colors.nightDark};
    min-width: 0;
`;

const PresetRow = styled.div`
    display: flex;
    gap: 8px;
    margin-bottom: 24px;
`;

const PresetChip = styled.button`
    border: 1.5px solid ${({ $active }) => $active ? campaign.purple : `${colors.night}22`};
    background: ${({ $active }) => $active ? `${campaign.purple}14` : 'transparent'};
    color: ${({ $active }) => $active ? campaign.purple : colors.night};
    font-size: 12.5px;
    font-weight: 700;
    padding: 6px 12px;
    border-radius: 50px;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        border-color: ${campaign.purple};
    }
`;

const Table = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const TableHead = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: 8px;
    padding: 0 0 8px;
    border-bottom: 1px solid ${colors.night}1a;
    margin-bottom: 10px;

    span {
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.02em;
        color: ${colors.nightLight};
    }

    @media screen and (min-width: ${breakpoints.smd}) {
        span {
            font-size: 12px;
        }
    }
`;

const TableRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: 8px;
    align-items: center;
    padding: 8px 0;

    & + & {
        border-top: 1px dashed ${colors.night}14;
    }
`;

const MethodLabel = styled.span`
    font-size: 13px;
    font-weight: 700;
    color: ${colors.nightDark};

    @media screen and (min-width: ${breakpoints.smd}) {
        font-size: 14.5px;
    }
`;

const OldFee = styled.span`
    font-size: 12.5px;
    font-weight: 600;
    color: ${colors.nightLight};
    text-decoration: line-through;

    @media screen and (min-width: ${breakpoints.smd}) {
        font-size: 14px;
    }
`;

const NewFee = styled.span`
    display: flex;
    flex-direction: column;
    font-size: 13px;
    font-weight: 800;
    color: ${campaign.purple};

    @media screen and (min-width: ${breakpoints.smd}) {
        font-size: 15px;
    }
`;

const RateTag = styled.span`
    font-size: 10.5px;
    font-weight: 700;
    color: ${campaign.purpleDeep};
    opacity: 0.7;
`;

const Savings = styled.span`
    font-size: 13px;
    font-weight: 800;
    color: #157F3C;

    @media screen and (min-width: ${breakpoints.smd}) {
        font-size: 15px;
    }
`;
